import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { AiOutlineEye } from "react-icons/ai";
import { BsChevronDown, BsThreeDotsVertical } from "react-icons/bs";
import { FaRegTrashCan } from "react-icons/fa6";
import { FiEdit } from "react-icons/fi";
import {
    ButtonHeading,
    CommonDateRange,
    CommonInput,
    CustomPagination,
} from "components";
import BootstrapTable from "components/BootstrapTable";
import { Images } from "constants";
import { utils } from "helper";
import { assignTransactionApi, getAllDropdownTripApi, getAllVehicleNumber, getfastagListApi, getfastagTransactionListApi } from "_services";
import { StorageServices } from "utils";
import "../Fastags/Fastags.css";
import SingleSelectDropdownInfinite from "components/common/SingleSelectDropdownInfinite/SingleSelectDropdownInfinite";
import StatusDropdown from "../StatusDropdown";
import { constant } from 'constants/constant';
import Tab from "components/common/Tab";
import { ConfirmModalFor } from "modals";
import useDocumentTitle from "hooks/useDocumentTitle";

const FastagsTransaction = () => {
    // Constants
    const ITEMS_PER_PAGE = 25;
    const DEBOUNCE_DELAY = 500; // 500ms delay
    const companyId = StorageServices?.getCompanyId();
    useDocumentTitle("Fastag Transactions | T360");
    // Initial filter object
    const initialFilterObj = {
        company_id: companyId,
        start_date: "",
        end_date: "",
        search: "",
        limit: ITEMS_PER_PAGE,
        offset: 1,
        vehicle_id: "",
        txntype: "",
        trip_relation: 0
    };

    // State management
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const tabFromQuery = parseInt(queryParams.get('tab')) || 1;

    const [activeTab, setActiveTab] = useState(tabFromQuery);
    const [selectedRange, setSelectedRange] = useState({
        startDate: "",
        endDate: "",
    });
    const [filter, setFilter] = useState(initialFilterObj);
    const [searchInput, setSearchInput] = useState(""); // Separate state for search input
    const [fastagList, setFastagList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalItems, setTotalItems] = useState(0);
    const [resetKey, setResetKey] = useState(0);
    const navigate = useNavigate();
    const [activeTransactionTab, setActiveTransactionTab] = useState(0);
    const isInitialMount = useRef(true);
    const [isStatusChange, setIsStatusChange] = useState({
        isOpen: "",
        tag: ""
    })
    const [tripDetails, setTripDetails] = useState({})
    const [isLoading, setIsLoading] = useState(false)
    // Table columns configuration
    const columns = [
        { header: "S.No.", accessor: "sn" },
        { header: "Transaction Date", accessor: "txndt" },
        { header: "Assigned", accessor: "fastag_number" },
        { header: "Transaction ID", accessor: "txnno" },
        { header: "Description", accessor: "tripDescription" },
        { header: "Type", accessor: "fastag_status" },
        { header: "Amount", accessor: "credit_debit" },
        { header: "Opening Bal.", accessor: "balance" },
        { header: "Closing Bal.", accessor: "closingBal" },
        { header: "Status", accessor: "status_dropdown" },
    ];

    const statusOptions = [
        { value: 2, label: 'Assigned', color: '#2563EB', bgColor: '#EFF6FF' },
        { value: 1, label: 'Unassigned', color: '#6B7280', bgColor: '#F9FAFB' },
        { value: 3, label: 'Omitted', color: '#059669', bgColor: '#ECFDF5' },
    ];

    // Debounce effect for search
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        const delayTimer = setTimeout(() => {
            setFilter((prev) => ({
                ...prev,
                search: searchInput,
                offset: 1,
            }));
        }, DEBOUNCE_DELAY);

        return () => clearTimeout(delayTimer);
    }, [searchInput]);

    // Effects
    useEffect(() => {
        fetchFastagList();
    }, [filter, activeTab]);

    // API call
    const fetchFastagList = async () => {
        setLoading(true);
        try {
            const apiPayload = {
                ...filter,
                vehicle_id: filter?.vehicle_id?._id,
                // Transaction type filter based on top tabs
                txntype: activeTab === 2 ? "D" : activeTab === 3 ? "C" : "",
                                firm_id:StorageServices?.getFirmId()||""
                
            };

            const res = await getfastagTransactionListApi(apiPayload);


            if (res?.responseCode === 200) {
                setFastagList(res.data?.list || []);
                setTotalItems(res.data || 0);
            }
        } catch (error) {
            console.error("Error fetching fastag list:", error);
            setFastagList([]);
            setTotalItems(0);
        } finally {
            setLoading(false);
        }
    };

    const handleApply = (newRange) => {
        setSelectedRange(newRange);
        setFilter((prev) => ({
            ...prev,
            start_date: moment(newRange.startDate).format("YYYY-MM-DD"),
            end_date: moment(newRange.endDate).format("YYYY-MM-DD"),
            offset: 1,
        }));
    };

    const handleReset = () => {
        const resetFilter = {
            ...initialFilterObj,
            start_date: "", // moment().startOf("month").format("YYYY-MM-DD"),
            end_date: "", // moment().endOf("month").format("YYYY-MM-DD"),
        };
        setFilter(resetFilter);
        setSearchInput(""); // Reset search input
        setSelectedRange({
            startDate: "", // moment().startOf("month"),
            endDate: "", // moment().endOf("month"),
        });
        setActiveTransactionTab(0);
        setResetKey((prev) => prev + 1);
    };

    const handlePageChange = (page) => {
        setFilter((prev) => ({ ...prev, offset: page }));
    };

    const handleTransactionTabChange = (tab) => {
        setActiveTransactionTab(tab);
        setFilter((prev) => ({
            ...prev,
            offset: 1,
            trip_relation: tab
        }));
    };


    const fetchVehicle = async (search, page, limit) => {
        const res = await getAllVehicleNumber({
            company_id: companyId,
            offset: page,
            limit,
            search,
            vehicle_ownership: [1, 3]
        });
        if (res?.responseCode === 200) {
            const list = res?.data?.vehicle_list?.map((i) => ({
                ...i,
                name: `${i?.vehicle_no}`,
                ownershipStatus: i?.vehicle_ownership,
            }));
            return {
                items: list,
                total: res?.data?.total_record,
            };
        } else {
            return { items: [], total: 0 };
        }
    };



    const fetchTrip = async (search, page = 1, limit = 25) => {
        const res = await getAllDropdownTripApi({
            company_id: companyId,
            vehicle_id: isStatusChange?.isOpen?.assigned_to_vehicle?._id,
            offset: page,
            limit,
            search,
        });
        if (res?.responseCode === 200) {
            const list = res?.data?.list?.map((i) => ({
                ...i,
                name: `${i?.trip_number}`,
                id: i?._id
            }));
            return {
                items: list,
                total: res?.data?.total_record,
            };
        } else {
            return { items: [], total: 0 };
        }
    };

    const StatusChangeHandle = async () => {
        const payload = {
            fastag_txn_id: isStatusChange?.isOpen?._id,
            trip_id: tripDetails?._id ? tripDetails?._id : isStatusChange?.isOpen?.trip?._id || null,
            trip_relation: isStatusChange?.tag
        }
        setIsLoading(true)
        try {
            const res = await assignTransactionApi(payload)

            if (res?.responseCode == 200) {
                fetchFastagList()
                setIsStatusChange((pre) => ({ ...pre, isOpen: false, tag: "" }))
                setTripDetails({})
            }
        } catch (error) {

        } finally {
            setIsLoading(false)
        }
    }

    // Transform data for table rows
    const rowListData = (data,offset=0) => {
        return data?.map((item, i) => {
            // Calculate balances properly
            const openingBalance = Number(item?.balance) || 0;
            const debitAmount = Number(item?.debitamt) || 0;
            const creditAmount = Number(item?.creditamt) || 0;

            // CORRECT CALCULATION:
            // For DEBIT: Closing = Opening - Debit (money going out)
            // For CREDIT: Closing = Opening + Credit (money coming in)
            const closingBalance = item?.txntype === "D"
                ? openingBalance - debitAmount
                : openingBalance + creditAmount;

            return {
                ...item,
             sn: offset + i + 1,
                serial: (
                    <React.Fragment>
                        <div className="signDropdown">
                            <button className="dropbtn11">
                                <BsThreeDotsVertical />
                            </button>
                            <div className="dropdownContent">
                                <Link
                                    className="_avPointer"
                                    style={{ textDecoration: "none" }}
                                >
                                    <span className="_samAnchor">
                                        <span className="viewIcon_avt">
                                            <AiOutlineEye />
                                        </span>
                                        &nbsp; View
                                    </span>
                                </Link>
                                <Link className="_avPointer" style={{ textDecoration: "none" }}>
                                    <span className="_samAnchor">
                                        <span className="viewIcon_avt">
                                            <FiEdit />
                                        </span>
                                        &nbsp; Edit
                                    </span>
                                </Link>
                                <div className="_avPointer">
                                    <span className="_samAnchor">
                                        <span className="deleteIcon_avt">
                                            <FaRegTrashCan color="red" />
                                        </span>
                                        Delete
                                    </span>
                                </div>
                            </div>
                        </div>
                    </React.Fragment>
                ),
                addition_date: (
                    <React.Fragment>
                        {item?.createdAt
                            ? utils?.formatUiDateTime(item?.createdAt)
                            : "-"}
                    </React.Fragment>
                ),
                assigned_to_company: (
                    <React.Fragment>
                        <div className="">
                            {item?.assigned_to_company
                                ? item.assigned_to_company?.company
                                : "+ Assign to Company"}
                        </div>
                    </React.Fragment>
                ),
                assigned_to_vehicle: (
                    <React.Fragment>
                        <div className="imgSel_box"></div>
                    </React.Fragment>
                ),
                balance: (
                    <React.Fragment>
                        <div className="imgSel_box">
                            {openingBalance ? `₹ ${openingBalance.toFixed(2)}` : "₹ 0.00"}
                        </div>
                    </React.Fragment>
                ),
                credit_debit: (
                    <React.Fragment>
                        <div className="imgSel_box">
                            {item?.txntype === "D"
                                ? `₹ ${debitAmount.toFixed(2)}`
                                : `₹ ${creditAmount.toFixed(2)}`}
                        </div>
                    </React.Fragment>
                ),
                closingBal: (
                    <React.Fragment>
                        <div className="imgSel_box">
                            ₹ {closingBalance.toFixed(2)}
                        </div>
                    </React.Fragment>
                ),
                fastag_tags: (
                    <React.Fragment>
                        <div className="imgSel_box">
                            {item?.fastag_tags || "-"}
                        </div>
                    </React.Fragment>
                ),
                fastag_status: (
                    <React.Fragment>
                        <div className="imgSel_box">
                            {item?.txntype === "D" ? "DEBIT" : "CREDIT"}
                        </div>
                    </React.Fragment>
                ),
                fastag_number: (
                    <React.Fragment>
                        <div className="imgSel_box">
                            {item?.assigned_to_vehicle?.vehicle_no || "-"}
                        </div>
                        {/* <div className="__fastNumber">
                            <p>{item?.tagid || "-"}</p>
                        </div> */}
                    </React.Fragment>
                ),
                updatedAt: (
                    <React.Fragment>
                        {item?.updatedAt ? utils?.formatUiDateTime(item?.updatedAt) : "-"}
                    </React.Fragment>
                ),
                status_dropdown: (
                    <React.Fragment>
                        <StatusDropdown
                            value={item?.trip_relation}
                            onChange={(newStatus) => item?.trip_relation != newStatus && setIsStatusChange({ isOpen: item, tag: newStatus })}
                            options={statusOptions}
                            size="medium"
                            disabled={item?.txntype === "C"}
                        />
                    </React.Fragment>
                ),

                tripDescription: (
                    <React.Fragment>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <div style={{ fontWeight: 400, fontSize: '14px', color: '#1a1a1a' }}>
                                {item?.txndetails || "-"}
                            </div>
                            <Link to={`/trip/addtrip/dispatched/${item?.trip?._id}?t=freight`}>
                                {item?.trip?.trip_number ? `Trip #${item?.trip?.trip_number}` : ""}
                            </Link>
                            <div style={{ fontSize: '12px', color: '#888' }}>
                                {item?.trip?.route_name || ""}
                            </div>
                        </div>
                    </React.Fragment>
                ),
            };
        });
    };

    const tabProps = {
        tabList: constant.fastDetailsTabs,
        activeTab: activeTab,
        callBackTab: (e) => {
            setActiveTab(e);
        },
    };

    const statusChangeProps = {
        show: isStatusChange?.isOpen,
        closeModal: () => setIsStatusChange((pre) => ({ ...pre, isOpen: false, tag: "" })),
        title: isStatusChange?.tag == 1 ? "Unassign Transaction" : isStatusChange?.tag == 3 ? "Omit Transaction" : "Associate Transactions",
        submitTitle: isStatusChange?.tag == 2 ? "Add Transactions" : "Save",
        onSubmit: StatusChangeHandle,
        confirmModal: "confirmModal",
        icon: null,
        isLoadding: isLoading
    };



    return (
        <>
            <ButtonHeading title="Fastag Transactions" />
            <div className='addPr_flBx __sulpFlx_main'>
                <Tab {...tabProps} />
                <div className="__companyPage_mainBx">
                    <div
                        className="__companyPage_filterRow"
                        style={{ borderBottom: "0px", marginBottom: "0px" }}
                    >
                        <div className="transaction-tabs">
                            <button
                                className={`transaction-tab ${activeTransactionTab === 0 ? "active" : ""}`}
                                onClick={() => handleTransactionTabChange(0)}
                            >
                                All
                            </button>
                            <button
                                className={`transaction-tab ${activeTransactionTab === 2 ? "active" : ""}`}
                                onClick={() => handleTransactionTabChange(2)}
                            >
                                Assigned
                            </button>
                            <button
                                className={`transaction-tab ${activeTransactionTab === 1 ? "active" : ""}`}
                                onClick={() => handleTransactionTabChange(1)}
                            >
                                Unassigned
                            </button>
                            <button
                                className={`transaction-tab ${activeTransactionTab === 3 ? "active" : ""}`}
                                onClick={() => handleTransactionTabChange(3)}
                            >
                                Omitted
                            </button>
                        </div>
                        <div style={{ flex: "0 0 220px" }}>
                            <CommonDateRange
                                key={resetKey}
                                initialStartDate={selectedRange.startDate}
                                initialEndDate={selectedRange.endDate}
                                datePrefield={!selectedRange.startDate && !selectedRange.endDate ? false : true}
                                onApply={handleApply}
                            />
                        </div>
                        <div style={{ flex: "0 0 220px" }}>
                            <SingleSelectDropdownInfinite
                                placeholder="Vehicle Number"
                                fetchData={fetchVehicle}
                                selectedItem={filter?.vehicle_id}
                                onChange={(data) => {
                                    setFilter((pre) => ({ ...pre, vehicle_id: data, offset: 1 }))
                                }}
                                loadOnOpen={true}
                                padding={"7px"}
                                vehicleStatus={false}
                            />
                        </div>
                        <div style={{ flex: "0 0 289px" }}>
                            <CommonInput
                                placeholder="Search Transaction ID"
                                label="Search Transaction ID"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                            />
                        </div>
                        <button className="anime-btn __reSet_btn" onClick={handleReset}>
                            Reset
                        </button>
                    </div>

                    {/* Statistics Section */}
                    <div className="__fastag__count__bx">
                        <div className="__fastag__count__inrbx">
                    <span>Transactions</span>
                    <span className="__fastag__count__display">{totalItems?.total_record || 0}</span>
                </div>
                <div className="__fastag__count__inrbx">
                    <span>Total Debit</span>
                    <span className="__fastag__count__display">{`₹ ${Number(totalItems?.total_debit || 0).toFixed(2)}`}</span>
                </div>
                <div className="__fastag__count__inrbx">
                    <span>Total Credit</span>
                    <span className="__fastag__count__display">{`₹ ${Number(totalItems?.total_credit || 0).toFixed(2)}`}</span>
                </div>
                    </div>

                    {/* Table Section */}
                    <div className="_tabWith_outTab">
                        <BootstrapTable
                            tableClass="companyTable_bx"
                            columns={columns}
                            data={rowListData(fastagList?.length > 0 ? fastagList : [],(filter.offset - 1) * ITEMS_PER_PAGE)}
                            isLoading={loading}
                        />
                        {fastagList?.length > 0 && (
                            <div className="paginationFt_avt " style={{ borderBottom: 'none' }}>
                                <CustomPagination
                                    totalItems={totalItems?.total_record || 1}
                                    itemsPerPage={ITEMS_PER_PAGE}
                                    currentPage={filter.offset}
                                    onPageChange={handlePageChange}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {isStatusChange.isOpen && (
                <ConfirmModalFor {...statusChangeProps}>
                    <>
                        {isStatusChange?.tag == 2 && <div className="colFlx">
                            {/* <div className="_addRouts_flex"> */}
                            <CommonInput
                                label="Vehicle Number"
                                placeholder="Vehicle Number"
                                name="vehicle_id"
                                value={isStatusChange?.isOpen?.assigned_to_vehicle?.vehicle_no}
                                disabled
                            />
                            {/* </div> */}
                            <div className="_addRouts_flex">
                                <SingleSelectDropdownInfinite
                                    label="Trip"
                                    placeholder="Select Trip"
                                    fetchData={fetchTrip}
                                    selectedItem={tripDetails}
                                    onChange={(data) => {
                                        setTripDetails(data)
                                    }}
                                    loadOnOpen={true}

                                />
                            </div>


                        </div>}
                        {isStatusChange?.tag == 3 && <p className="_confirmPrx"> Are you sure you want to omit this transaction?</p>}
                        {isStatusChange?.tag == 1 && <p className="_confirmPrx"> Are you sure you want to unassign this transaction?</p>}

                    </>
                </ConfirmModalFor>
            )}
        </>
    );
};

export default FastagsTransaction;